import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Container, Row, Col, Card, Button, Breadcrumb, Badge } from 'react-bootstrap';
import { 
  FaMapMarkerAlt, 
  FaCalendarAlt, 
  FaUser, 
  FaClock,
  FaCheckCircle,
  FaStar,
  FaArrowLeft,
  FaArrowRight
} from 'react-icons/fa';
import { projectsAPI } from '../utils/api';
import Loader from '../components/common/Loader';
import './ProjectDetail.css';

const ProjectDetail = () => {
  const { slug } = useParams();
  const [project, setProject] = useState(null);
  const [relatedProjects, setRelatedProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchProject();
    window.scrollTo(0, 0);
  }, [slug]);

  const fetchProject = async () => {
    try {
      setLoading(true);
      const response = await projectsAPI.getBySlug(slug);
      setProject(response.data.data);
      setRelatedProjects(response.data.relatedProjects || []);
      document.title = `${response.data.data.title} | Infrastructure Company`;
    } catch (err) {
      console.error('Error fetching project:', err);
      setError('Project not found');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <Loader />;
  }

  if (error || !project) {
    return (
      <Container className="text-center py-5">
        <h2>Project Not Found</h2>
        <p className="text-muted mb-4">The project you're looking for doesn't exist.</p>
        <Button as={Link} to="/projects" variant="primary">
          View All Projects
        </Button>
      </Container>
    );
  }

  return (
    <div className="project-detail-page">
      {/* Breadcrumb */}
      <section className="breadcrumb-section">
        <Container>
          <Breadcrumb>
            <Breadcrumb.Item linkAs={Link} linkProps={{ to: '/' }}>
              Home
            </Breadcrumb.Item>
            <Breadcrumb.Item linkAs={Link} linkProps={{ to: '/projects' }}>
              Projects
            </Breadcrumb.Item>
            <Breadcrumb.Item active>{project.title}</Breadcrumb.Item>
          </Breadcrumb>
        </Container>
      </section>

      {/* Project Header */}
      <section className="project-detail-header">
        <Container>
          <Row>
            <Col lg={8}>
              <div className="d-flex flex-wrap gap-2 mb-3">
                <Badge bg="primary" className="category-badge">
                  {project.category}
                </Badge>
                {project.status && (
                  <Badge 
                    bg={
                      project.status === 'Completed' ? 'success' : 
                      project.status === 'Ongoing' ? 'warning' : 'info'
                    }
                    className="status-badge"
                  >
                    {project.status}
                  </Badge>
                )}
                {project.isFeatured && (
                  <Badge bg="danger" className="featured-badge">
                    <FaStar /> Featured
                  </Badge>
                )}
              </div>
              
              <h1 className="display-5 fw-bold mb-3">{project.title}</h1>
              <p className="lead text-muted mb-4">
                {project.shortDescription || project.description}
              </p>

              {/* Project Meta Info */}
              <Row className="g-3 project-meta-row">
                <Col sm={6} md={3}>
                  <div className="meta-box">
                    <FaMapMarkerAlt className="meta-icon" />
                    <div>
                      <small className="d-block text-muted">Location</small>
                      <strong>{project.location?.city}, {project.location?.state}</strong>
                    </div>
                  </div>
                </Col>
                <Col sm={6} md={3}>
                  <div className="meta-box">
                    <FaUser className="meta-icon" />
                    <div>
                      <small className="d-block text-muted">Client</small>
                      <strong>{project.client?.name || project.client}</strong>
                    </div>
                  </div>
                </Col>
                <Col sm={6} md={3}>
                  <div className="meta-box">
                    <FaClock className="meta-icon" />
                    <div>
                      <small className="d-block text-muted">Duration</small>
                      <strong>{project.projectDetails?.duration || project.duration}</strong>
                    </div>
                  </div>
                </Col>
                <Col sm={6} md={3}>
                  <div className="meta-box">
                    <FaCalendarAlt className="meta-icon" />
                    <div>
                      <small className="d-block text-muted">Completed</small>
                      <strong>
                        {project.projectDetails?.completionDate 
                          ? new Date(project.projectDetails.completionDate).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' })
                          : project.completionDate 
                          ? new Date(project.completionDate).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' })
                          : 'N/A'}
                      </strong>
                    </div>
                  </div>
                </Col>
              </Row>
            </Col>
            <Col lg={4} className="text-lg-end mt-4 mt-lg-0">
              <Button as={Link} to="/contact" variant="primary" size="lg">
                Start Similar Project
              </Button>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Project Images */}
      <section className="section">
        <Container>
          <Row>
            <Col lg={8}>
              {/* Main Image */}
              <div className="main-image-wrapper mb-4">
                <img
                  src={project.thumbnail}
                  alt={project.title}
                  className="img-fluid rounded shadow-lg"
                />
              </div>

              {/* Additional Images */}
              {project.images && project.images.length > 0 && (
                <Row className="g-3 mb-4">
                  {project.images.slice(0, 4).map((image, index) => (
                    <Col key={index} xs={6} md={3}>
                      <div className="gallery-image-wrapper">
                        <img
                          src={typeof image === 'string' ? image : image.url}
                          alt={`${project.title} - ${index + 1}`}
                          className="img-fluid rounded"
                        />
                      </div>
                    </Col>
                  ))}
                </Row>
              )}

              {/* Project Description */}
              <Card className="mb-4">
                <Card.Body className="p-4">
                  <h3 className="mb-3">Project Overview</h3>
                  <p className="text-muted" style={{ lineHeight: '1.8' }}>
                    {project.description}
                  </p>
                </Card.Body>
              </Card>

              {/* Project Highlights */}
              {project.highlights && project.highlights.length > 0 && (
                <Card className="mb-4">
                  <Card.Body className="p-4">
                    <h3 className="mb-4">Project Highlights</h3>
                    <Row className="g-3">
                      {project.highlights.map((highlight, index) => (
                        <Col key={index} md={6}>
                          <div className="highlight-item">
                            <FaCheckCircle className="highlight-icon" />
                            <span>{highlight}</span>
                          </div>
                        </Col>
                      ))}
                    </Row>
                  </Card.Body>
                </Card>
              )}

              {/* Technologies Used */}
              {project.technologies && project.technologies.length > 0 && (
                <Card className="mb-4">
                  <Card.Body className="p-4">
                    <h3 className="mb-3">Technologies & Methods Used</h3>
                    <div className="d-flex flex-wrap gap-2">
                      {project.technologies.map((tech, index) => (
                        <Badge key={index} bg="light" text="dark" className="tech-badge">
                          {tech}
                        </Badge>
                      ))}
                    </div>
                  </Card.Body>
                </Card>
              )}

              {/* Challenges & Solutions */}
              {(project.challenges || project.solutions) && (
                <Card className="mb-4">
                  <Card.Body className="p-4">
                    {project.challenges && project.challenges.length > 0 && (
                      <div className="mb-4">
                        <h4 className="mb-3">Challenges Faced</h4>
                        <ul className="custom-list">
                          {project.challenges.map((challenge, index) => (
                            <li key={index}>{challenge}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    
                    {project.solutions && project.solutions.length > 0 && (
                      <div>
                        <h4 className="mb-3">Our Solutions</h4>
                        <ul className="custom-list">
                          {project.solutions.map((solution, index) => (
                            <li key={index}>{solution}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </Card.Body>
                </Card>
              )}
            </Col>

            {/* Sidebar */}
            <Col lg={4}>
              {/* Project Details Card */}
              <Card className="project-info-card mb-4 sticky-top" style={{ top: '100px' }}>
                <Card.Body className="p-4">
                  <h4 className="mb-4">Project Details</h4>
                  
                  <div className="info-item">
                    <strong>Category:</strong>
                    <span>{project.category}</span>
                  </div>

                  <div className="info-item">
                    <strong>Status:</strong>
                    <span>
                      <Badge 
                        bg={
                          project.status === 'Completed' ? 'success' : 
                          project.status === 'Ongoing' ? 'warning' : 'info'
                        }
                      >
                        {project.status}
                      </Badge>
                    </span>
                  </div>

                  <div className="info-item">
                    <strong>Location:</strong>
                    <span>{project.location?.address || `${project.location?.city}, ${project.location?.state}`}</span>
                  </div>

                  <div className="info-item">
                    <strong>Client:</strong>
                    <span>{project.client?.name || project.client}</span>
                  </div>

                  {project.client?.organization && (
                    <div className="info-item">
                      <strong>Organization:</strong>
                      <span>{project.client.organization}</span>
                    </div>
                  )}

                  <div className="info-item">
                    <strong>Duration:</strong>
                    <span>{project.projectDetails?.duration || project.duration}</span>
                  </div>

                  {project.projectDetails?.projectValue && (
                    <div className="info-item">
                      <strong>Project Value:</strong>
                      <span>{project.projectDetails.projectValue}</span>
                    </div>
                  )}

                  {project.projectDetails?.areaSize && (
                    <div className="info-item">
                      <strong>Area Size:</strong>
                      <span>{project.projectDetails.areaSize}</span>
                    </div>
                  )}

                  {project.rating && (
                    <div className="info-item">
                      <strong>Rating:</strong>
                      <span>
                        {[...Array(project.rating)].map((_, i) => (
                          <FaStar key={i} className="text-warning" />
                        ))}
                      </span>
                    </div>
                  )}

                  <hr />

                  <Button as={Link} to="/contact" variant="primary" className="w-100">
                    Discuss Your Project
                  </Button>
                </Card.Body>
              </Card>

              {/* Share Card */}
              <Card className="share-card">
                <Card.Body className="p-4">
                  <h5 className="mb-3">Interested in This Project?</h5>
                  <p className="text-muted mb-3">
                    Contact us to discuss similar infrastructure solutions for your needs.
                  </p>
                  <Button as={Link} to="/contact" variant="outline-primary" className="w-100">
                    Get in Touch
                  </Button>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Related Projects */}
      {relatedProjects && relatedProjects.length > 0 && (
        <section className="section bg-light">
          <Container>
            <div className="section-header">
              <h2 className="section-title">Related Projects</h2>
              <p className="section-description">
                Other projects in the same category
              </p>
            </div>

            <Row className="g-4">
              {relatedProjects.slice(0, 3).map((relProject) => (
                <Col key={relProject._id} md={6} lg={4}>
                  <Link to={`/projects/${relProject.slug}`} className="text-decoration-none">
                    <Card className="related-project-card h-100">
                      <div className="related-img-wrapper">
                        <img
                          src={relProject.thumbnail}
                          alt={relProject.title}
                          className="card-img-top"
                        />
                      </div>
                      <Card.Body>
                        <Badge bg="primary" className="mb-2">{relProject.category}</Badge>
                        <h5 className="card-title">{relProject.title}</h5>
                        <p className="text-muted small mb-3">
                          <FaMapMarkerAlt /> {relProject.location?.city}, {relProject.location?.state}
                        </p>
                        <span className="text-primary fw-semibold">
                          View Project <FaArrowRight />
                        </span>
                      </Card.Body>
                    </Card>
                  </Link>
                </Col>
              ))}
            </Row>

            <div className="text-center mt-4">
              <Button as={Link} to="/projects" variant="outline-primary">
                View All Projects
              </Button>
            </div>
          </Container>
        </section>
      )}

      {/* Navigation */}
      <section className="section">
        <Container>
          <div className="d-flex justify-content-between">
            <Button as={Link} to="/projects" variant="outline-primary">
              <FaArrowLeft /> Back to Projects
            </Button>
            <Button as={Link} to="/contact" variant="primary">
              Start Your Project
            </Button>
          </div>
        </Container>
      </section>

      <div className="indian-accent"></div>
    </div>
  );
};

export default ProjectDetail;